import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthContext } from '../../context/AuthContext'
import LandingNav from '../common/LandingNav/LandingNav'

import './Landing.css'

const Landing = () => {
  const { isAuthenticated } = useAuthContext()
  const navigate = useNavigate()
  const [selectedUseCase, setSelectedUseCase] = useState(0)
  const [demoStep, setDemoStep] = useState(0)

  const handleEnterApp = () => {
    // Mark that user has visited the app
    localStorage.setItem('has-visited-app', 'true')
    navigate('/app')
  }

  const handleSignIn = () => {
    navigate('/auth')
  }

  const handleBookDemo = () => {
    // Scroll to contact section or open demo modal
    const contactSection = document.querySelector('#contact')
    if (contactSection) {
      const headerHeight = 80
      const targetPosition = contactSection.offsetTop - headerHeight
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      })
    } else {
      // If no contact section, navigate to home
      navigate('/home')
    }
  }

  // Title animation
  useEffect(() => {
    const titleLines = document.querySelectorAll('.hero-title .title-line')
    
    // Animate title lines
    titleLines.forEach((line, index) => {
      setTimeout(() => {
        line.style.opacity = '1'
        line.style.transform = 'translateY(0)'
      }, index * 300 + 500)
    })
    
  }, [])

  // Smooth scrolling for navigation links
  useEffect(() => {
    const handleSmoothScroll = (e) => {
      const href = e.target.getAttribute('href')
      if (href && href.startsWith('#')) {
        e.preventDefault()
        const target = document.querySelector(href)
        if (target) {
          const headerHeight = 80 // Approximate header height
          const targetPosition = target.offsetTop - headerHeight
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          })
        }
      }
    }

    const links = document.querySelectorAll('a[href^="#"]')
    links.forEach(link => {
      link.addEventListener('click', handleSmoothScroll)
    })

    return () => {
      links.forEach(link => {
        link.removeEventListener('click', handleSmoothScroll)
      })
    }
  }, [])

  // Demo animation
  useEffect(() => {
    const demoFeatures = document.querySelectorAll('.demo-feature')
    
    const animateFeatures = () => {
      demoFeatures.forEach((feature, index) => {
        setTimeout(() => {
          feature.style.opacity = '1'
          feature.style.transform = 'translateX(0)'
        }, index * 500 + 2000) // Start after 2 seconds, 500ms between each
      })
    }

    // Initialize features as hidden
    demoFeatures.forEach(feature => {
      feature.style.opacity = '0'
      feature.style.transform = 'translateX(-20px)'
      feature.style.transition = 'all 0.3s ease'
    })

    // Start animation
    const timer = setTimeout(animateFeatures, 1000)

    return () => clearTimeout(timer)
  }, [])

  const features = [
    {
      icon: 'fas fa-brain',
      title: 'AI Supervisor',
      description: 'Advanced LLM-powered supervisor that processes and responds based on stored memory and context, ensuring accurate and relevant conversations every time.'
    },
    {
      icon: 'fas fa-database',
      title: 'Memory Context',
      description: 'Intelligent knowledge retrieval system that learns from your conversations and maintains context across sessions for truly personalized interactions.'
    },
    {
      icon: 'fas fa-users',
      title: 'Multi-Agent Collaboration',
      description: 'Leverage multiple AI agents working together to provide comprehensive, well-rounded answers from different perspectives and expertise areas.'
    },
    {
      icon: 'fas fa-exchange-alt',
      title: 'Cross-LLM Search',
      description: 'Query multiple LLM providers simultaneously including OpenAI, Anthropic, and Google to get the most comprehensive and accurate results.'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Secure & Private',
      description: 'Your conversations and data are protected with enterprise-grade security. All processing happens securely with full privacy controls.'
    },
    {
      icon: 'fas fa-cogs',
      title: 'Highly Configurable',
      description: 'Customize your AI experience with flexible settings, agent configurations, and memory management to suit your specific needs.'
    }
  ]

  const useCases = useMemo(() => [
    {
      icon: 'fas fa-chart-line',
      title: 'Sales & Marketing Teams',
      description: 'Maintain context across customer conversations with multiple LLMs. Track campaign strategies, competitor analysis, and customer insights seamlessly across ChatGPT, Claude, and Gemini interactions.',
      benefits: ['40% faster campaign planning', 'Consistent messaging across teams'],
      demo: {
        scenario: 'Marketing Campaign Planning',
        messages: [
          { type: 'user', text: 'What was our Q3 campaign ROI analysis?', delay: 0 },
          { type: 'system', text: 'Searching memory + context...', delay: 600 },
          { type: 'ai', text: 'Found Q3 analysis: 23% ROI increase with social media focus. Your engagement data shows 40% higher performance on visual content vs text-based campaigns.', delay: 1400 },
          { type: 'user', text: 'What do the AIs suggest for Q4?', delay: 2200 },
          { type: 'ai', text: 'ChatGPT recommends TikTok expansion, Claude suggests Instagram Reels focus, Gemini proposes video-first strategy. All align with your visual content strength - creating integrated Q4 approach...', delay: 3000 }
        ],
        features: ['Context + LLM Synthesis', 'Brand Voice Integration', 'Behavioral Data Analysis']
      }
    },
    {
      icon: 'fas fa-code',
      title: 'Development Teams',
      description: 'Keep technical discussions and code reviews in context. Share architectural decisions, bug fixes, and feature requirements across different AI models while maintaining project continuity.',
      benefits: ['60% reduction in context switching', 'Better code documentation'],
      demo: {
        scenario: 'Code Review & Architecture',
        messages: [
          { type: 'user', text: 'What did we decide about the database schema with GPT-4?', delay: 0 },
          { type: 'system', text: 'Analyzing decisions + codebase context...', delay: 600 },
          { type: 'ai', text: 'GPT-4 recommended PostgreSQL with normalized tables. Claude suggested indexes for performance. Gemini proposed caching. Your current tech stack and data patterns suggest PostgreSQL + Redis hybrid approach.', delay: 1400 },
          { type: 'user', text: 'Show me the implementation plan', delay: 2200 },
          { type: 'ai', text: 'Creating roadmap using AI insights + your existing infrastructure, team expertise, and deployment constraints...', delay: 3000 }
        ],
        features: ['Codebase Context Analysis', 'Infrastructure Alignment', 'Team Capability Matching']
      }
    },
    {
      icon: 'fas fa-users-cog',
      title: 'Customer Support',
      description: 'Provide consistent, context-aware support by accessing previous customer interactions across all AI platforms. Ensure support agents have complete conversation history and resolution patterns.',
      benefits: ['50% faster resolution times', 'Improved customer satisfaction'],
      demo: {
        scenario: 'Customer Issue Resolution',
        messages: [
          { type: 'user', text: 'Customer John Smith having login issues again', delay: 0 },
          { type: 'system', text: 'Loading customer profile + AI insights...', delay: 600 },
          { type: 'ai', text: 'Previous case: 2FA reset worked. Claude suggested password policy update. Your customer data shows John prefers email, is tech-savvy, and logs in from mobile 80% of the time.', delay: 1400 },
          { type: 'user', text: 'Apply the same solution', delay: 2200 },
          { type: 'ai', text: 'Initiating 2FA reset with mobile-optimized flow and personalized email based on customer behavior patterns...', delay: 3000 }
        ],
        features: ['Customer Profile Analysis', 'Behavioral Pattern Recognition', 'Personalized Solution Matching']
      }
    },
    {
      icon: 'fas fa-lightbulb',
      title: 'Research & Strategy',
      description: 'Consolidate market research, competitive analysis, and strategic planning across multiple AI consultations. Build comprehensive knowledge bases from various LLM insights.',
      benefits: ['70% more comprehensive analysis', 'Data-driven decision making'],
      demo: {
        scenario: 'Market Research Analysis',
        messages: [
          { type: 'user', text: 'Compile competitor analysis from all AI discussions', delay: 0 },
          { type: 'system', text: 'Synthesizing AI insights + market context...', delay: 600 },
          { type: 'ai', text: 'ChatGPT identified 5 competitors, Claude analyzed pricing, Gemini found gaps. Your industry position and customer feedback reveal unique differentiation opportunities.', delay: 1400 },
          { type: 'user', text: 'What are the strategic opportunities?', delay: 2200 },
          { type: 'ai', text: 'Combining AI analysis with your company strengths, market position, and resource capabilities to identify 3 tailored strategic opportunities...', delay: 3000 }
        ],
        features: ['Market Position Analysis', 'Company Strength Integration', 'Contextual Opportunity Mapping']
      }
    },
    {
      icon: 'fas fa-graduation-cap',
      title: 'Training & Education',
      description: 'Create personalized learning experiences by maintaining context across educational conversations. Track learning progress and adapt content based on previous AI interactions.',
      benefits: ['Personalized learning paths', '45% better knowledge retention'],
      demo: {
        scenario: 'Personalized Learning',
        messages: [
          { type: 'user', text: 'Continue my Python learning from yesterday', delay: 0 },
          { type: 'system', text: 'Analyzing learning profile + AI recommendations...', delay: 600 },
          { type: 'ai', text: 'You mastered functions with ChatGPT, struggled with loops per Claude. Gemini suggested visuals. Your learning style shows 70% visual preference and morning peak performance.', delay: 1400 },
          { type: 'user', text: 'Yes, but use visual examples like Gemini suggested', delay: 2200 },
          { type: 'ai', text: 'Creating personalized Python classes lesson with visual diagrams, adapted to your pace, interests, and optimal learning times...', delay: 3000 }
        ],
        features: ['Learning Style Analysis', 'Performance Pattern Recognition', 'Personalized Content Adaptation']
      }
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Legal & Compliance',
      description: 'Maintain secure, auditable records of AI consultations for legal research, contract analysis, and compliance reviews. Ensure consistency across different AI model recommendations.',
      benefits: ['Complete audit trails', 'Regulatory compliance'],
      demo: {
        scenario: 'Legal Document Analysis',
        messages: [
          { type: 'user', text: 'Review contract terms discussed with legal AIs', delay: 0 },
          { type: 'system', text: 'Cross-referencing legal database + AI insights...', delay: 600 },
          { type: 'ai', text: 'GPT-4 flagged clause 7.3, Claude suggested liability limits, Gemini found precedents. Your company risk profile and jurisdiction requirements indicate additional IP protections needed.', delay: 1400 },
          { type: 'user', text: 'Generate compliance report', delay: 2200 },
          { type: 'ai', text: 'Creating tailored compliance report combining AI recommendations with your legal framework, industry standards, and regulatory requirements...', delay: 3000 }
        ],
        features: ['Risk Profile Integration', 'Regulatory Context Matching', 'Company Policy Alignment']
      }
    }
  ], [])

  // Demo animation effect — must come after useCases declaration
  useEffect(() => {
    if (selectedUseCase !== null) {
      setDemoStep(0)
      const demo = useCases[selectedUseCase]?.demo
      if (demo) {
        let currentStep = 0
        const animateMessages = () => {
          if (currentStep < demo.messages.length) {
            setTimeout(() => {
              setDemoStep(currentStep + 1)
              currentStep++
              animateMessages()
            }, demo.messages[currentStep]?.delay || 800)
          }
        }
        setTimeout(animateMessages, 300)
      }
    }
  }, [selectedUseCase, useCases])

  return (
    <div className="landing">
      {/* Header */}
      <LandingNav />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-main">
            {/* Left Side - Content */}
            <div className="hero-content">
              <div className="hero-badge fade-in-up">
                <i className="fas fa-sparkles"></i>
                <span>Next-Generation AI Platform</span>
              </div>
              <h1 className="hero-title">
                <span className="title-line">The Next Generation of AI Conversations Starts Here</span>
              </h1>
              <p className="hero-subtitle fade-in-up">
                Experience the future of AI conversations with OmniContext AI. Our platform combines contextual memory, multi-agent collaboration, and cross-LLM search to deliver unprecedented intelligence and accuracy.
              </p>
              
              {/* CTA Buttons */}
              <div className="hero-content-buttons">
                {isAuthenticated ? (
                  <button onClick={handleEnterApp} className="btn btn-primary">
                    <i className="fas fa-sign-in-alt"></i>
                    <span>Sign In</span>
                  </button>
                ) : (
                  <button onClick={handleSignIn} className="btn btn-primary">
                    <i className="fas fa-sign-in-alt"></i>
                    <span>Get Started</span>
                  </button>
                )}
                <button onClick={handleBookDemo} className="btn btn-demo">
                  <i className="fas fa-calendar-alt"></i>
                  <span>Book a Demo</span>
                </button>
              </div>
            </div>

            {/* Right Side - Visual Demo */}
            <div className="hero-visual">
              <div className="demo-container">
                <div className="demo-header">
                  <div className="demo-controls">
                    <div className="demo-dot red"></div>
                    <div className="demo-dot yellow"></div>
                    <div className="demo-dot green"></div>
                  </div>
                  <span className="demo-title">OmniContext AI</span>
                </div>
                <div className="demo-content">
                  <div className="demo-message user-message">
                    <div className="message-avatar user-avatar">
                      <i className="fas fa-user"></i>
                    </div>
                    <div className="message-content">
                      <p>What did we discuss about the Q4 marketing strategy last week?</p>
                    </div>
                  </div>
                  <div className="demo-message ai-message">
                    <div className="message-avatar supervisor-avatar">
                      <i className="fas fa-brain"></i>
                    </div>
                    <div className="message-content">
                      <p><strong>Supervisor:</strong> From your context on Nov 15th: Q4 strategy focused on AI-driven personalization, budget allocation discussions, and target audience refinement. Your team preferred video content and had concerns about ROI tracking. Now consulting agents for deeper analysis...</p>
                    </div>
                  </div>
                  <div className="demo-message ai-message">
                    <div className="message-avatar network-avatar">
                      <i className="fas fa-network-wired"></i>
                    </div>
                    <div className="message-content">
                      <p><strong>Multi-Agent Network:</strong></p>
                      <div className="agent-recommendations">
                        <div className="agent-rec"><strong>Claude</strong> (Nov 12th): "Focus on LinkedIn video ads, 40% budget increase."</div>
                        <div className="agent-rec"><strong>ChatGPT</strong> (Nov 10th): "TikTok expansion for Gen Z, test micro-influencers."</div>
                        <div className="agent-rec"><strong>Gemini</strong> (Nov 8th): "Implement dynamic retargeting with AI personalization."</div>
                      </div>
                      <p><em>Synthesis: All suggest video-first approach aligning with your team's preference.</em></p>
                      <div className="typing-animation">
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                      </div>
                    </div>
                  </div>
                  <div className="demo-features">
                    <div className="demo-feature">
                      <i className="fas fa-history"></i>
                      <span>Previous LLM Conversations</span>
                    </div>
                    <div className="demo-feature">
                      <i className="fas fa-exchange-alt"></i>
                      <span>3 Agents Cross-Referenced</span>
                    </div>
                    <div className="demo-feature">
                      <i className="fas fa-lightbulb"></i>
                      <span>Contextual Recommendations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-header">
          <h2>Powerful Features</h2>
          <p>Discover what makes OmniContext AI the most advanced conversation platform available today</p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">
                <i className={feature.icon}></i>
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases" id="use-cases">
        <div className="use-cases-container">
          <div className="section-header">
            <h2>Transform Your Business Operations</h2>
            <p>See how leading companies are leveraging OmniContext AI to revolutionize their workflows</p>
          </div>
          
          <div className="use-cases-interactive">
            {/* Use Case Selector */}
            <div className="use-case-selector">
              {useCases.map((useCase, index) => (
                <div 
                  key={index}
                  className={`use-case-tab ${selectedUseCase === index ? 'active' : ''}`}
                  onClick={() => setSelectedUseCase(index)}
                >
                  <div className="use-case-tab-icon">
                    <i className={useCase.icon}></i>
                  </div>
                  <div className="use-case-tab-content">
                    <h3>{useCase.title}</h3>
                    <p>{useCase.description}</p>
                    <div className="use-case-benefits">
                      {useCase.benefits.map((benefit, benefitIndex) => (
                        <span key={benefitIndex}>
                          <i className="fas fa-check"></i> {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Demo Visualization */}
            <div className="use-case-demo">
              <div className="demo-header">
                <div className="demo-controls">
                  <div className="demo-dot red"></div>
                  <div className="demo-dot yellow"></div>
                  <div className="demo-dot green"></div>
                </div>
                <span className="demo-title">
                  {useCases[selectedUseCase]?.demo.scenario || 'Select a use case'}
                </span>
                <button 
                  className="demo-replay"
                  onClick={() => setSelectedUseCase(selectedUseCase)}
                >
                  <i className="fas fa-redo"></i>
                </button>
              </div>
              
              <div className="demo-content">
                {useCases[selectedUseCase]?.demo.messages.slice(0, demoStep).map((message, index) => (
                  <div key={index} className={`demo-message ${message.type}-message`}>
                    <div className={`message-avatar ${message.type}-avatar`}>
                      <i className={`fas fa-${message.type === 'user' ? 'user' : message.type === 'ai' ? 'robot' : 'cog'}`}></i>
                    </div>
                    <div className="message-content">
                      <p>{message.text}</p>
                      {message.type === 'ai' && index === demoStep - 1 && index < useCases[selectedUseCase]?.demo.messages.length - 1 && (
                        <div className="typing-animation">
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                          <div className="typing-dot"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {demoStep >= useCases[selectedUseCase]?.demo.messages.length && (
                  <div className="demo-features">
                    {useCases[selectedUseCase]?.demo.features.map((feature, index) => (
                      <div key={index} className="demo-feature">
                        <i className="fas fa-check-circle"></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="benefits-container">
          <div className="section-header">
            <h2>Why Choose OmniContext AI?</h2>
            <p>Transform your AI workflow with cutting-edge technology</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-zap"></i>
              </div>
              <h3>Instant Setup</h3>
              <p>Get started in minutes, not hours. No complex configuration required.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-lock"></i>
              </div>
              <h3>Privacy First</h3>
              <p>Your conversations and data remain completely private and secure.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-infinity"></i>
              </div>
              <h3>No Limits</h3>
              <p>Unlimited conversations with multiple AI models simultaneously.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-graduation-cap"></i>
              </div>
              <h3>Learns & Remembers</h3>
              <p>Builds context from your conversations for smarter responses.</p>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="cta-section" id="cta">
        <div className="cta-content">
          <h2>Ready to Experience the Future of AI?</h2>
          <p>Start your journey with OmniContext AI today. No credit card required, no setup fees, just powerful AI conversations with contextual memory.</p>
          <div className="cta-final-buttons">
            <button onClick={handleEnterApp} className="btn btn-white">
              <i className="fas fa-rocket"></i>
              <span>Start Free Trial</span>
            </button>
            <button className="btn btn-outline">
              <i className="fas fa-calendar-alt"></i>
              <span>Schedule Demo</span>
            </button>
          </div>
          <p className="cta-note">
            <i className="fas fa-check-circle"></i>
            Free forever plan available • No setup required • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>OmniContext AI</h3>
            <p>The most advanced AI conversation platform with contextual memory, multi-agent collaboration, and cross-LLM search capabilities.</p>
          </div>
          <div className="footer-section">
            <h3>Features</h3>
            <p><a href="#features">AI Supervisor</a></p>
            <p><a href="#features">Memory Context</a></p>
            <p><a href="#features">Multi-Agent</a></p>
            <p><a href="#features">Cross-LLM Search</a></p>
          </div>
          <div className="footer-section">
            <h3>Support</h3>
            <p><a href="#">Documentation</a></p>
            <p><a href="#">API Reference</a></p>
            <p><a href="#">Community</a></p>
            <p><a href="#">Contact</a></p>
          </div>
          <div className="footer-section">
            <h3>Company</h3>
            <p><Link to="/aboutUs">About Us</Link></p>
            <p><Link to="/simpleDisclosure">Simple Disclosure</Link></p>
            <p><Link to="/policy">Privacy Policy</Link></p>
            <p><Link to="/legalTerms">Terms of Service</Link></p>
            
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 OmniContext AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

export default Landing
